// Moving in from the app's old address (moved/, server/move.ts). The page there sealed everything a phone saved with a
// key that travels only in the move code; here the sealed copy is fetched by the code's id, opened with its key, and
// each saved value is put back under this address's names. Arriving by link (?move=id#key=…) it happens at once; an
// iPhone app added afresh to the Home Screen is given the code to type or paste.
import { type Nights, cleanNights, fromLogbook, mergeNights } from '../live/nightsCore';
import { STORE, cleanPlace } from '../state';

/** The app's first address. Its page (moved/index.html), opened with ?bring, sends everything saved there to this one
 * at once; with nothing saved there, it sends the person straight back, ?bring and all. */
export const FIRST_ADDRESS = 'https://nocturne-sky.pages.dev';

/** Crockford's base 32: no I, L, O or U. */
const ALPHABET = /^[0-9A-HJKMNP-TV-Z]+$/;

/** A code as people may type it: any case, with spaces or dashes, and O for 0, I or L for 1. */
export function splitCode(typed: string): { id: string; key: string } | null {
  const c = typed.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
  return c.length === 16 && ALPHABET.test(c) ? { id: c.slice(0, 6), key: c.slice(6) } : null;
}

/** The code to carry across, in groups of four. */
export const formatCode = (id: string, key: string) => `${id}${key}`.match(/.{4}/g)!.join('-');

/** A move arriving by link: the id in the address, the key after # (never sent to any server). */
export function moveFromAddress(loc: { search: string; hash: string } = location): { id: string; key: string } | null {
  const id = new URLSearchParams(loc.search).get('move') ?? '';
  const key = new URLSearchParams(loc.hash.slice(1)).get('key') ?? '';
  return splitCode(id + key);
}

export interface MovePayload { v: 1; at: string; keys: Record<string, string>; notifications?: boolean }

/** Why a move did not open: the code is wrong or has run out ('code'), the server could not be reached ('network'), this
 * browser cannot unpack it ('old'), or this phone would not store what came ('storage'). */
export class MoveError extends Error {
  constructor(readonly why: 'code' | 'network' | 'old' | 'storage') {
    super(why);
  }
}

const fromBase64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/** The sealed copy, fetched and opened. */
export async function openMove(id: string, key: string, get: typeof fetch = fetch): Promise<MovePayload> {
  let r: Response;
  try {
    r = await get(`/api/move/${id}`);
  } catch {
    throw new MoveError('network');
  }
  if (r.status === 404) throw new MoveError('code');
  if (!r.ok) throw new MoveError('network');
  try {
    const { iv, ct, z } = (await r.json()) as { iv: string; ct: string; z?: boolean };
    const raw = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`ahillah-move:${key}`));
    const k = await crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['decrypt']);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromBase64(iv) }, k, fromBase64(ct));
    if (z && typeof DecompressionStream === 'undefined') throw new MoveError('old');
    const text = z
      ? await new Response(new Blob([plain]).stream().pipeThrough(new DecompressionStream('gzip'))).text()
      : new TextDecoder().decode(plain);
    const p = JSON.parse(text) as MovePayload;
    if (p?.v !== 1 || !p.keys || typeof p.keys !== 'object') throw new Error('shape');
    return p;
  } catch (e) {
    // The wrong key cannot open it; nor can a copy that has been tampered with.
    throw e instanceof MoveError ? e : new MoveError('code');
  }
}

const parse = (s: string | null | undefined): unknown => {
  try {
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

/** An install from before nights were kept has only its "Seen it" log: its nights are that log as it would have brought
 *  them in there (src/live/nights.ts), each thing seen from the place saved there. Null for an install with nights. */
function nightsFromLog(keys: MovePayload['keys']): Nights | null {
  const log = parse(keys.logbook);
  if (parse(keys.nights) !== null || !log || typeof log !== 'object' || Array.isArray(log)) return null;
  return fromLogbook(log as Record<string, { t: number; place: string }>, cleanPlace(parse(keys.place)));
}

/**
 * Put what was saved at the old address in place here, and note the move. At an address not yet in use, everything comes
 * across ('all'), and the first start here moves it on from the version it was saved by, as an update would have there.
 * Where Ahillah is already in use, nothing here is replaced: the old nights are added to these ('nights'), so an old link,
 * a second move or a forged one can never wipe what was saved here. If this phone will not store it all (its storage
 * full, or none allowed), MoveError('storage'): tried again, it is the same move.
 */
export function applyMove(p: MovePayload, storage?: Pick<Storage, 'getItem' | 'setItem'>): 'all' | 'nights' {
  try {
    storage ??= localStorage;
    const inUse = storage.getItem(`${STORE}onboarded`) !== null;
    if (inUse) {
      const old = nightsFromLog(p.keys) ?? cleanNights(parse(p.keys.nights));
      const merged = mergeNights(cleanNights(parse(storage.getItem(`${STORE}nights`))), old);
      storage.setItem(`${STORE}nights`, JSON.stringify(merged.nights));
    } else {
      // Everything but the mark of an address in use, which comes last.
      for (const [k, v] of Object.entries(p.keys)) {
        if (k !== 'onboarded' && typeof v === 'string' && /^[\w.:-]{1,80}$/.test(k)) storage.setItem(STORE + k, v);
      }
      // This address wrote its own empty nights as it first opened, which would stop the log ever being brought in.
      const log = nightsFromLog(p.keys);
      if (log) storage.setItem(`${STORE}nights`, JSON.stringify(log));
      // What else it decided for itself then gives way where the old install had not decided it, so the first start here
      // decides it as an update would have there: the clean sky (for settings from before it), then the one-time offer of
      // Arabic names (src/ui/OnceOffer.tsx), and the update's news. A saved null reads as nothing saved.
      for (const k of [...(p.keys.settings != null ? ['cleanSky'] : []), 'offers', 'news']) {
        if (p.keys[k] == null && storage.getItem(STORE + k) !== null) storage.setItem(STORE + k, 'null');
      }
    }
    storage.setItem(`${STORE}moved`, JSON.stringify({ at: Date.now(), notifications: !!p.notifications }));
    // Only now, with everything else in place, is Ahillah in use here: a move that stopped part way (the phone's storage
    // full for a moment) is taken up again whole, never as a move into an address in use.
    if (!inUse && typeof p.keys.onboarded === 'string') storage.setItem(`${STORE}onboarded`, p.keys.onboarded);
    return inUse ? 'nights' : 'all';
  } catch (e) {
    throw e instanceof DOMException ? new MoveError('storage') : e;
  }
}
